import { AditionClientConfig, PermissionData, PermissionModel } from '../../interfaces';
import { CONFIG_ADITION, CONFIG_ENVIRONMENT, CONFIG_VERSION } from '../../config';
import { ConsentParameters } from '../../client/interfaces';

/**
 * @function getEndpoint
 * @description Function to get the ADITION endpoint from the given "portal" config.
 * @param {AditionClientConfig['portal']} portal ADITION "portal" parameter.
 * @return {string}
 */
export function getEndpoint(portal: AditionClientConfig['portal']): string {
  return CONFIG_ADITION.ENDPOINT[portal] || CONFIG_ADITION.DEFAULT.ENDPOINT;
}

/**
 * @function getPermissionStates
 * @description Function to get the permission states and wrap them into a string for the ADITION request.
 * @param {PermissionModel} permissionModel
 * @return {string}
 */
export function getPermissionStates(permissionModel: PermissionModel): string {
  let permissionStates = '';

  // No `permissionData`.
  if (!permissionModel || !permissionModel.data || permissionModel.data.length === 0) return permissionStates;

  for (let i = 0; i < permissionModel.data.length; i++) {
    const currentPermission: PermissionData = permissionModel.data[i];

    if (Object.prototype.hasOwnProperty.call(currentPermission, 'usecase') &&
      Object.prototype.hasOwnProperty.call(currentPermission, 'status')) {
      let status: string;
      switch(currentPermission.status) {
        case 'INVALID':
          status = '0';
          break;
        case 'VALID':
          status = '1';
          break;
        default:
          status = 'undefined';
      }
      permissionStates = `${permissionStates}&prf[${CONFIG_ADITION.PRF_PARAM_PREFIX.CONSENT}_${currentPermission.usecase}]=${status}`;
    }
  }

  return permissionStates;
}

/**
 * @async
 * @function getParameters
 * @description Function to get custom consent parameters from the publishers page to be appended to the ADITION request.
 * @return {string}
 */
export function getConsentParametersString(consentParameters: ConsentParameters): string {
  let consentParameterString = '';

  if (!consentParameters || Object.keys(consentParameters).length === 0) return consentParameterString;

  for (const _key in consentParameters) {
    consentParameterString = `${consentParameterString}&prf[${CONFIG_ADITION.PRF_PARAM_PREFIX.CONSENT_PARAMETER}_${_key}]=${encodeURIComponent(consentParameters[_key])}`;
  }

  return consentParameterString;
}

/**
 * @function getCacheBuster
 * @description Function to get a random generated number to prevent caching, ready to be appended to the request.
 * @return {string}
 */
export function getCacheBuster(): string {
  return Math.floor(Math.random() * Date.now()).toString();
}

/**
 * @function buildAditionRequest
 * @description Function to build the adition request url (base url, cachebuster and required params)
 * @param clientConfig configuration defined by the publisher
 * @param permissionModel current permission model
 * @param consentParameters consent parameters that will be passed on the layer
 * @param windowId random string that will be appended to the ADITION request to circumvent the browser cache
 * @param identity Clients identity
 * @example https://plus.gmx.net/lt?wpt=x&nw=42&lt=portal(tam)category(homepage)section(homepage)tagid(top)layoutclass(b)&lang=de&wi=1424131313
 * @return {string}
 */
export function buildAditionRequest(clientConfig: AditionClientConfig,
                                    permissionModel?: PermissionModel,
                                    consentParameters?: ConsentParameters,
                                    windowId?: string,
                                    identity?: string): string {

  if (!clientConfig.portal || !clientConfig.category) {
    throw new Error(`buildAditionRequest: clientConfig must have 'portal' and 'category' properties!`);
  }

  let stage = 'live';
  if (clientConfig.stage) {
    stage = clientConfig.stage;
  } else if (String(CONFIG_ENVIRONMENT) === 'QA') {
    stage = 'qa';
  }

  return getEndpoint(clientConfig.portal) +
    '/lt?wpt='            + CONFIG_ADITION.DEFAULT.REQUEST_TYPE +
    '&nw='                + CONFIG_ADITION.DEFAULT.NETWORK + '&lt=' +
    'portal('             + clientConfig.portal + ')' +
    'category('           + clientConfig.category + ')' +
    'section('            + (clientConfig.section || clientConfig.category) + ')' +
    'tagid('              + (clientConfig.tagid || CONFIG_ADITION.DEFAULT.TAGID) + ')' +
    'layoutclass('        + (clientConfig.layoutclass || CONFIG_ADITION.DEFAULT.LAYOUTCLASS) + ')' +
    '&ref='               + (document.referrer ? encodeURIComponent(new URL(document.referrer).origin) : '') + 
    '&external_uid='      + (identity ? identity : '') +
    '&prf[external_uid]=' + (identity ? identity : '') +
    '&prf[portal]='       + clientConfig.portal +
    '&prf[category]='     + clientConfig.category +
    '&prf[section]='      + (clientConfig.section || clientConfig.category) +
    '&prf[tagid]='        + (clientConfig.tagid || CONFIG_ADITION.DEFAULT.TAGID) +
    '&prf[layoutclass]='  + (clientConfig.layoutclass || CONFIG_ADITION.DEFAULT.LAYOUTCLASS) +
    '&prf[version]='      + (CONFIG_VERSION.indexOf('.') > -1 ? CONFIG_VERSION : '') +
    '&prf[stage]='        + stage +
    `${clientConfig.permissionGroup ? '&prf[permission_group]=' + clientConfig.permissionGroup : ''}` +
    (permissionModel ? getPermissionStates(permissionModel) : '') +
    (consentParameters ? getConsentParametersString(consentParameters)  : '') +
    `&wi=${windowId || getCacheBuster()}`;
}
